/**
 * Custom hook untuk mengambil detail booking berdasarkan ID.
 * 
 * API Endpoint: GET `/api/availability`
 * API Version: 3.0
 * 
 * @param {string} id - ID booking
 * @param {string} property_id - ID property
 * @param {string} channex_id - ID channex
 * @param {string} name - Nama room type
 * @param {string} search - Query search
 * @param {string} orderBy - Urutan berdasarkan field
 * @param {"asc" | "desc"} sort - Urutan ascending atau descending
 * @param {number} limit - Limit data
 * @param {boolean} all - Get all data
 * @returns {object} - Return value berupa object dengan key `data`, `isLoading`, dan `error`.
 * @example
 * const { data, isLoading, error } = useGetAllRoomType({ id: "1" });
 * const { data, isLoading, error } = useGetAllRoomType({ property_id: "1" });
 * const { data, isLoading, error } = useGetAllRoomType({ channex_id: "1" });
 * const { data, isLoading, error } = useGetAllRoomType({ name: "Deluxe", search: "Deluxe", orderBy: "name", sort: "asc", limit: 10});
 */

import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { GetAllRoomTypeResponseSchema } from "../response/GetAllRoomTypeResponse";

const API_VERSION = "v4";

interface GetAllRoomTypeProps {
    id?: string;
    property_id?: string;
    channex_id?: string;
    name?: string;
    search?: string;
    orderBy?: string;
    sort?: "asc" | "desc";
    limit?: number;
    all?: boolean;
}

const useGetAllRoomType = (params: GetAllRoomTypeProps = {}) => {
    const queryKey = [
        "bookings",
        "booking",
        "calendar",
        ...Object.entries(params)
            .filter(([_, value]) => value !== undefined && value !== null && value !== "")
            .map(([key, value]) => `${key}:${value}`)
    ];

    return useQuery({
        queryKey,
        queryFn: async () => {
            try {
                const { all, ...filters } = params;

                const response = await privateApi.get(`/${API_VERSION}/room-type`, {
                    params: all ? { all: 1 } : { filter: filters },
                });

                // Validasi response
                const validationResult = GetAllRoomTypeResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid Roomtype data format");
                }
                return validationResult.data;
            } catch (error) {
                console.error(`Failed to fetch Roomtype`, error);
                throw error;
            }
        },
        retry: import.meta.env.VITE_MAX_RETRY,
        refetchOnWindowFocus: false
    });
};

export default useGetAllRoomType;